/**
 * StatusPill — One line of progress for work the user may want to open: a picture, a title, a fill
 * behind them and the percent; when done, a check and one action.
 *
 * - Shown by `toast.status` in the bottom-left status stack; errors never use it.
 * - `percent: null` shows an indeterminate shimmer instead of a fill.
 */
import * as React from "react";

import { Button } from "@replohq/lattice/components/button/Button";
import { cn } from "@replohq/lattice/utils/cn";
import { Check, Loader2 } from "lucide-react";

const PILL_CLASS =
  "relative flex h-[40px] w-full items-center gap-2 overflow-hidden rounded-full border-0.5 border-border bg-sheet pl-1.5 text-sm text-default shadow-[0_10px_26px_-14px_rgb(0_0_0_/_0.35)]";

export type StatusPillAction = { label: string; onClick: () => void };

function StatusPillImage({ image }: { image: string | null | undefined }) {
  const [failedImage, setFailedImage] = React.useState<string | null>(null);
  if (!image || failedImage === image) {
    return (
      <span className="flex size-7 shrink-0 items-center justify-center">
        <Loader2 className="size-4 animate-spin text-muted" aria-hidden />
      </span>
    );
  }
  return (
    <img
      src={image}
      alt=""
      className="size-7 shrink-0 rounded-full border-0.5 border-border bg-surface object-cover object-top"
      onError={() => setFailedImage(image)}
    />
  );
}

export function StatusPill({
  title,
  percent = null,
  image,
  onClick,
}: {
  title: React.ReactNode;
  percent?: number | null;
  image?: string | null;
  onClick?: () => void;
}) {
  const content = (
    <>
      {percent === null ? (
        <span
          aria-hidden
          className="absolute inset-0 animate-pulse bg-[color-mix(in_srgb,var(--color-primary)_6%,transparent)]"
        />
      ) : (
        <span
          aria-hidden
          className="absolute inset-y-0 left-0 bg-[color-mix(in_srgb,var(--color-primary)_9%,transparent)] transition-[width] duration-500 ease-out"
          style={{ width: `${percent}%` }}
        />
      )}
      <span className="relative">
        <StatusPillImage image={image} />
      </span>
      <span className="relative min-w-0 flex-1 truncate text-left">
        {title}
      </span>
      {percent !== null && (
        <span className="relative shrink-0 pr-3.5 font-mono text-xs tabular-nums text-muted">
          {Math.round(percent)}%
        </span>
      )}
    </>
  );
  const Root = onClick ? "button" : "div";
  return (
    <Root
      {...(onClick ? { type: "button" as const, onClick } : {})}
      role="status"
      aria-live="polite"
      className={cn(
        PILL_CLASS,
        onClick &&
          "cursor-pointer hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        percent === null && "pr-3.5",
      )}
    >
      {content}
    </Root>
  );
}

export function StatusPillDone({
  title,
  action,
}: {
  title: React.ReactNode;
  action?: StatusPillAction;
}) {
  return (
    <div role="status" aria-live="polite" className={cn(PILL_CLASS, "pr-1")}>
      <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-emerald text-white">
        <Check className="size-4" aria-hidden />
      </span>
      <span className="min-w-0 flex-1 truncate">{title}</span>
      {action && (
        <Button
          variant="secondary"
          size="base"
          onClick={action.onClick}
          shape="pill"
        >
          {action.label}
        </Button>
      )}
    </div>
  );
}
